import { useCallback, useEffect, useRef, useState } from 'react';
import type { Page } from './api';

/**
 * A newest-first list that loads a page at a time.
 *
 * The API pages every list at 25, and the screens used to render only the first
 * page - the 26th role or candidate could not be reached at all. `reload` starts
 * again from the top, which is what you want after creating something, since the
 * new row is the newest; `loadMore` appends the next page.
 *
 * `fetchPage` must be stable (a module-level function or a `useCallback`), or
 * every render starts the list over.
 */
export function usePagedList<T>(fetchPage: (cursor?: string) => Promise<Page<T>>) {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>();

  // Bumped by every reload, so a page that was in flight when the list started
  // over is dropped rather than appended to the new listing.
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    try {
      const page = await fetchPage();
      if (current !== generation.current) return;
      setItems(page.items);
      setCursor(page.cursor);
      setError(undefined);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    const current = generation.current;
    setLoadingMore(true);
    try {
      const page = await fetchPage(cursor);
      if (current !== generation.current) return;
      setItems((existing) => [...existing, ...page.items]);
      setCursor(page.cursor);
      setError(undefined);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, fetchPage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // DynamoDB can hand back a cursor when the last page happened to be exactly
  // full; the next request then returns nothing and no cursor, and the button
  // goes away.
  return { items, hasMore: cursor !== undefined, loading, loadingMore, error, reload, loadMore };
}
